<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EnginePanel from './components/EnginePanel.vue';
import DefinitionTransfer from './components/DefinitionTransfer.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const flows = computed(() => store.getters['teletelFlows/getFlows']);
const meta = computed(() => store.getters['teletelFlows/getMeta']);
const uiFlags = computed(() => store.getters['teletelFlows/getUIFlags']);

onMounted(() => store.dispatch('teletelFlows/get'));

const goTo = name => router.push({ name });
const editFlow = flow =>
  router.push({ name: 'teletel_flows_edit', params: { flowId: flow.id } });

const publishFlow = async flow => {
  try {
    await store.dispatch('teletelFlows/publish', flow.id);
    useAlert(t('TELETEL_FLOWS.ACTIONS.PUBLISH_DONE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
const deactivateFlow = flow =>
  store.dispatch('teletelFlows/deactivate', flow.id);
const deleteFlow = async flow => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TELETEL_FLOWS.ACTIONS.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('teletelFlows/delete', flow.id);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.ACTIONS.ERROR')
    );
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6 overflow-auto">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.HEADER') }}
        </h1>
        <p class="text-sm text-n-slate-11 max-w-2xl">
          {{ t('TELETEL_FLOWS.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex gap-2">
        <NextButton slate sm @click="goTo('teletel_flows_runs')">
          {{ t('TELETEL_FLOWS.LIST.RUNS_PAGE') }}
        </NextButton>
        <NextButton slate sm @click="goTo('teletel_flows_errors')">
          {{ t('TELETEL_FLOWS.LIST.ERRORS_PAGE') }}
        </NextButton>
        <NextButton slate sm @click="goTo('teletel_flows_templates')">
          {{ t('TELETEL_FLOWS.TEMPLATES.LINK') }}
        </NextButton>
        <NextButton sm @click="goTo('teletel_flows_new')">
          {{ t('TELETEL_FLOWS.LIST.NEW_FLOW') }}
        </NextButton>
      </div>
    </div>

    <div
      v-if="meta.selectorErrors > 0"
      class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6"
    >
      {{
        t('TELETEL_FLOWS.SELECTOR_ALERT', {
          count: meta.selectorErrors,
          last: meta.selectorLastError || '—',
        })
      }}
    </div>

    <p v-if="!uiFlags.isFetching && !flows.length" class="text-n-slate-11">
      {{ t('TELETEL_FLOWS.LIST.404') }}
    </p>

    <table v-if="flows.length" class="w-full text-sm divide-y divide-n-weak">
      <thead>
        <tr class="text-start text-n-slate-11">
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.NAME') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.TRIGGER') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.STATUS') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.NODES') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.RUNS') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.LIST.TABLE.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr v-for="flow in flows" :key="flow.id">
          <td class="py-2 font-medium text-n-slate-12">{{ flow.name }}</td>
          <td class="py-2">
            {{ t(`TELETEL_FLOWS.TRIGGERS.${flow.trigger_type}`) }}
          </td>
          <td class="py-2">
            <span
              class="px-2 py-0.5 rounded-full text-xs"
              :class="
                flow.status === 'active'
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{ t(`TELETEL_FLOWS.STATUS.${flow.status}`) }}
            </span>
          </td>
          <td class="py-2">{{ flow.node_count }}</td>
          <td class="py-2">{{ flow.runs_count }}</td>
          <td class="py-2">
            <div class="flex gap-1">
              <NextButton slate xs @click="editFlow(flow)">
                {{ t('TELETEL_FLOWS.ACTIONS.EDIT') }}
              </NextButton>
              <NextButton
                v-if="flow.status === 'draft'"
                teal
                xs
                @click="publishFlow(flow)"
              >
                {{ t('TELETEL_FLOWS.ACTIONS.PUBLISH') }}
              </NextButton>
              <NextButton v-else amber xs @click="deactivateFlow(flow)">
                {{ t('TELETEL_FLOWS.ACTIONS.DEACTIVATE') }}
              </NextButton>
              <NextButton ruby xs @click="deleteFlow(flow)">
                {{ t('TELETEL_FLOWS.ACTIONS.DELETE') }}
              </NextButton>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <DefinitionTransfer @imported="store.dispatch('teletelFlows/get')" />
    <EnginePanel />
  </div>
</template>
